import { AsyncPipe } from '@angular/common';
import { Component, Input, OnChanges, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { Params, Router, RouterModule, UrlSerializer } from '@angular/router';
import { TranslocoModule, provideTranslocoScope } from '@jsverse/transloco';
import { WA_SESSION_STORAGE } from '@ng-web-apis/common';
import { VerticalType } from '@rma/common-ui-domain/types';
import { GeoPoint } from '@rma/generic/domain/types';
import { RmaIconComponent } from '@rma/generic/ui/icon';
import { StaticImageUrlPipe } from '@rma/generic/util/environment';
import { MapsService } from '@rma/maps/api';
import {
  DEFAULT_GOOGLE_STATIC_MAP_CONFIG,
  GOOGLE_MAPS_STATIC_CACHE_PREFIX,
  type GoogleStaticMapConfig,
  type MapMarkerType,
  type MapRequest,
  type RmaMapMarker,
} from '@rma/maps/domain';
import { StaticGoogleMapComponent } from '@rma/maps/public/orc-static-google-map';
import { CoordsToMapMarkerPipe } from '@rma/maps/util';
import { Observable, map, of } from 'rxjs';

const TRANSLATION_SCOPE = 'profileMapCta';

@Component({
  selector: 'rma-map-cta',
  imports: [AsyncPipe, MatButtonModule, RmaIconComponent, RouterModule, StaticGoogleMapComponent, StaticImageUrlPipe, TranslocoModule],
  providers: [MapsService, provideTranslocoScope(TRANSLATION_SCOPE), CoordsToMapMarkerPipe],
  templateUrl: './map-cta.component.html',
  host: {
    class: 'relative rounded-lg',
  },
})
export class MapCtaComponent implements OnChanges {
  private readonly coordsToMapMarkerPipe = inject(CoordsToMapMarkerPipe);
  private readonly mapsService = inject(MapsService);
  private readonly router = inject(Router);
  private readonly serializer = inject(UrlSerializer);
  private readonly sessionStorage = inject<Storage>(WA_SESSION_STORAGE);

  @Input({ required: true })
  mapUrl!: string;

  @Input({ required: true })
  mapType!: MapMarkerType;

  @Input({ required: true })
  verticalType!: VerticalType;

  @Input({ required: true })
  mapRequest!: Partial<MapRequest>;

  @Input()
  mapUrlParams: Params | null | undefined = null;

  @Input()
  target: '_self' | '_blank' = '_self';

  @Input()
  useHref = false;

  @Input()
  primaryLocation: GeoPoint | null | undefined;

  @Input()
  skipCache = false;

  protected readonly scope = TRANSLATION_SCOPE;

  protected mapConfig: GoogleStaticMapConfig = {
    ...DEFAULT_GOOGLE_STATIC_MAP_CONFIG,
    enableZoom: false,
    class: `${DEFAULT_GOOGLE_STATIC_MAP_CONFIG.class} rounded-lg`,
    cache: `${GOOGLE_MAPS_STATIC_CACHE_PREFIX}${this.router.url}`,
  };

  protected markers$: Observable<RmaMapMarker[]> | undefined = undefined;
  protected staticMapUrl?: string;
  protected href?: string;

  ngOnChanges(): void {
    if (this.skipCache) {
      this.mapConfig = { ...this.mapConfig, cache: undefined };
    }

    if (this.sessionStorage?.getItem && this.mapConfig.cache) {
      const cache = this.sessionStorage.getItem(this.mapConfig.cache);
      if (cache) {
        this.staticMapUrl = cache;
        this.markers$ = of([]);
        return;
      }
    }

    if ((this.verticalType && this.mapType, this.mapRequest)) {
      const markers$ = this.getMarkers(this.verticalType, this.mapType, this.mapRequest);

      if (this.primaryLocation) {
        this.markers$ = markers$.pipe(
          map((markers) => {
            markers.unshift(
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              this.coordsToMapMarkerPipe.transform(this.primaryLocation!, { mapType: 'CompletedListing', status: 'primary' })!,
            );

            return markers;
          }),
        );
      } else {
        this.markers$ = markers$;
      }
    }

    if (this.useHref) {
      this.href = this.getHref(this.mapUrl, this.mapUrlParams);
    }
  }

  private getMarkers(verticalType: VerticalType, mapType: MapMarkerType, params: Partial<MapRequest>): Observable<RmaMapMarker[]> {
    const mapRequest: MapRequest = {
      verticalType,
      markerType: mapType,
      take: 5,
      ...params,
    };

    return this.mapsService
      .queryMapMarkers(mapRequest)
      .pipe(
        map(({ markers }) =>
          markers
            .map((m) => this.coordsToMapMarkerPipe.transform(m.coords, { mapType: this.mapType, status: 'standard' }))
            .filter((m): m is RmaMapMarker => Boolean(m)),
        ),
      );
  }

  private getHref(mapUrl: string, mapUrlParams: Params | null | undefined): string {
    if (!mapUrlParams) {
      return mapUrl;
    }

    return `${mapUrl}?${this.serializer.serialize(this.router.createUrlTree(['.'], { queryParams: mapUrlParams }))}`.replace('?/', '');
  }
}
